import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as S from "./MediaFileSection.styles";
import MediaPlayer, { MediaItem } from "components/MediaPlayer";
import { formatDuration } from "utils/formatDuration";
import { ReactComponent as MediaIcon } from "@/assets/result/media_icon.svg?react";
import { ReactComponent as ArrowIcon } from "@/assets/result/arrow_icon.svg?react";
import FileUploader from "features/upload/FileUploader";
import styled from "styled-components";
import { ProcessResult as ProcessResultVu } from "../../../types/output_vu";
import { ProcessResultVh } from "../../../types/output_vh";
import { UploadStoreProvider } from "store/uploadStore";
import { useNotificationStore } from "store/notificationStore";
import { useModal } from "store/modalStore";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: transparent; // FileUploader의 배경을 사용
  padding: 20px;
  border-radius: 8px;
  width: 100%;
  max-width: 990px;
`;

interface MediaFileSectionProps {
  projectId: string;
  result: (ProcessResultVu | ProcessResultVh) | null;
}

export const MediaFileSection: React.FC<MediaFileSectionProps> = ({
  projectId,
  result,
}) => {
  const navigate = useNavigate();
  const DOWNLOAD_URL = import.meta.env.VITE_DOWNLOAD_URL;
  const { openConfirm } = useModal();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploaderActive, setIsUploaderActive] = useState(false);
  const { notification, clearNotification } = useNotificationStore();

  const mediaItems: MediaItem[] = useMemo(() => {
    if (!result) return [];
    const { vtt } = result.output;

    // YouTube URL로 업로드한 경우
    if (result.youtubeInfo?.metadata) {
      const { metadata, videoPath } = result.youtubeInfo;
      return [
        {
          id: metadata.id,
          title: metadata.title,
          src: `${DOWNLOAD_URL}/${videoPath}`,
          thumbnail: metadata.thumbnail,
          duration: formatDuration(metadata.duration),
          date: new Date(metadata.upload_date).toLocaleDateString(),
          type: "youtube",
          subtitleSrc: `${DOWNLOAD_URL}/${vtt}`,
        },
      ];
    }

    // 파일로 직접 업로드한 경우
    if (result.uploadFilename && result.output.mediaInfo) {
      const { uploadFilename, filePath, thumbnailPath } = result;

      return [
        {
          id: filePath,
          title: uploadFilename,
          src: `${DOWNLOAD_URL}/${filePath}`,
          thumbnail: thumbnailPath
            ? `${DOWNLOAD_URL}/${thumbnailPath}`
            : undefined,
          duration: formatDuration(
            parseFloat(result.output.mediaInfo.duration)
          ),
          date: new Date().toLocaleDateString(),
          type:
            result.output.mediaInfo.codec_type === "audio" ? "audio" : "video",
          subtitleSrc: `${DOWNLOAD_URL}/${vtt}`,
        },
      ];
    }

    return [];
  }, [result, DOWNLOAD_URL]);

  const handleAnalyzeOther = () => {
    setIsUploaderActive(true);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleGoToResult = () => {
    if (notification) {
      const { projectId, documentId, projectName, result } = notification;
      const newPath = `/results/${projectId}/${documentId}`;
      navigate(newPath, { state: { projectName, result } });
      clearNotification();
      setIsUploaderActive(false); // Clean up the uploader instance
    }
  };

  const handleCancelNotification = () => {
    clearNotification();
    setIsUploaderActive(false); // Clean up the uploader instance
  };

  return (
    <>
      {notification && (
        openConfirm({
          title: "분석 완료",
          message: "새 파일 분석이 완료되었습니다. 결과를 확인하시겠습니까?",
          onConfirm: handleGoToResult,
          onCancel: handleCancelNotification,
        })
      )}
      <S.Section>
        <S.Header>
          <S.Title>
            <MediaIcon />
            미디어파일
          </S.Title>
          <S.ActionButton onClick={handleAnalyzeOther}>
            다른 음원 분석하기
            <ArrowIcon />
          </S.ActionButton>
        </S.Header>

        {mediaItems.length > 0 && <MediaPlayer items={mediaItems} />}
      </S.Section>

      {isUploaderActive && (
        <div style={{ display: isModalOpen ? "flex" : "none" }}>
          <ModalOverlay onClick={handleCloseModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <UploadStoreProvider>
                <FileUploader projectId={projectId} />
              </UploadStoreProvider>
            </ModalContent>
          </ModalOverlay>
        </div>
      )}
    </>
  );
};
